import Box from '@mui/material/Box';
import type { FC } from 'react';
import { RiCalendarScheduleFill, RiCalendarScheduleLine } from 'react-icons/ri';

interface ScheduleToggleProps {
  /** True when a future publish time is set. */
  scheduled: boolean;
  onClick: () => void;
}

/**
 * Compact schedule button for the post composer (space admins only). Sits in
 * the left controls group immediately before the pin toggle and matches its
 * rectangular shape and borderless soft-grey fill: a quiet neutral pill at
 * rest, a deeper neutral fill with a filled calendar when a publish time is
 * set. Deliberately non-primary — only the Post button reads as primary in the
 * composer. Opening the picker is delegated to the caller via onClick.
 */
export const ScheduleToggle: FC<ScheduleToggleProps> = ({ scheduled, onClick }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    aria-pressed={scheduled}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 0.5,
      height: 34,
      px: 1.25,
      borderRadius: '10px',
      border: 'none',
      cursor: 'pointer',
      fontFamily: 'Figtree, sans-serif',
      fontSize: 13,
      fontWeight: 600,
      transition: 'background-color 0.15s ease, color 0.15s ease',
      ...(scheduled
        ? {
            bgcolor: '#eae4e2',
            color: '#5a5350',
            '&:hover': { bgcolor: '#e2dad7' },
          }
        : {
            // Transparent at rest with a soft-grey hover — matches the feed
            // filter trigger buttons and the other composer control pills.
            bgcolor: 'transparent',
            color: '#666',
            '&:hover': { bgcolor: '#f4f4f4' },
          }),
    }}
  >
    {scheduled ? <RiCalendarScheduleFill size={15} /> : <RiCalendarScheduleLine size={15} />}
    {scheduled ? 'Scheduled' : 'Schedule'}
  </Box>
);
